import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { IAgent, IGrantor, IFuturePowSetting, IDocumentData } from "../types";
import { recommendedSettings, settingIds, extendOptions, settingCategories } from "../constants";

interface IFuturePowState extends IDocumentData {
  isEditMode: boolean;
  identifier: string;
}

const initialState: IFuturePowState = {
  identifier: "",
  grantor: {
    fullname: "",
    ssn: "",
  },
  agents: [],
  settings: [
    { id: settingIds.extend, value: extendOptions.economic_personal, category: settingCategories.general },
    ...recommendedSettings,
  ],
  isEditMode: false,
};

const futurePowSlice = createSlice({
  name: "documentCreation",
  initialState,
  reducers: {
    setDocumentIdentifier(state, action: PayloadAction<IFuturePowState["identifier"]>) {
      state.identifier = action.payload;
    },
    setGrantorData(state, action: PayloadAction<Partial<IGrantor>>) {
      Object.assign(state.grantor, action.payload);
    },
    setAgentsData(state, action: PayloadAction<IAgent[]>) {
      state.agents = action.payload;
    },
    setSettingById(state, action: PayloadAction<IFuturePowSetting>) {
      const setting = state.settings.find((settingItem) => settingItem.id === action.payload.id);
      if (setting) {
        setting.value = action.payload.value;
      } else {
        state.settings.push(action.payload);
      }
    },
    setSettings(state, action: PayloadAction<IFuturePowSetting[]>) {
      const incomingItems = new Map(action.payload.map((settingItem) => [settingItem.id, settingItem]));
      const updated = state.settings.map((settingItem) => incomingItems.get(settingItem.id) ?? settingItem);

      state.settings = [
        ...updated,
        ...action.payload.filter((s) => !state.settings.some((existingSettingItem) => existingSettingItem.id === s.id)),
      ];
    },
    removeSettingById(state, action: PayloadAction<string>) {
      state.settings = state.settings.filter((settingItem) => settingItem.id !== action.payload);
    },
    setIsEditMode(state, action: PayloadAction<boolean>) {
      state.isEditMode = action.payload;
    },
  },
});

export const {
  setDocumentIdentifier,
  setGrantorData,
  setAgentsData,
  setSettingById,
  setSettings,
  removeSettingById,
  setIsEditMode,
} = futurePowSlice.actions;
export default futurePowSlice.reducer;
